import { faChevronLeft, faClose } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import type { To } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { Portal } from '#src/atoms/layout/Portal/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineL } from '#src/atoms/typography/index.tsx';
import { UserCurrencies } from '#src/components/navigation/AppNavigation/UserCurrencies/index.tsx';
import { UserNav } from '#src/components/navigation/UserNav/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useDeeplinkFromNativeApp } from '#src/hooks/useDeeplinkFromNativeApp.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { glossary } from '#src/lib/glossary.ts';
import { hasPreviousLocation } from '#src/lib/routing.tsx';

import { ProSetToggle } from '../AppNavigation/ProSetToggle';

const StyledLink = styled(Link)`
  overflow: hidden;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
  }
`;

const Title = styled(HeadlineL)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

type Props = {
  text?: MessageDescriptor | string;
  children?: ReactNode;
  backTo?: To;
  disableBackBrowser?: boolean;
  onClick?: () => void;
  customTitle?: ReactNode;
};
export const PageHeader = ({
  text,
  children,
  backTo,
  onClick,
  disableBackBrowser,
  customTitle,
}: Props) => {
  const {
    flags: { enableNavToggleProSet = false },
  } = useFeatureFlags();
  const { sport } = useSportContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isIosApp } = useIsMobileApp();
  const isLaptopAndAbove = useIsLaptop();
  const isLoggedIn = useIsLoggedIn();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const location = useLocation();

  if (!isLoggedIn) {
    return null;
  }

  // back browser can be disabled using prop or
  // is always disabled if user comes from global NavItem
  const shouldDisableBackBrowser =
    disableBackBrowser || location.state?.fromNavItem;

  const backButton =
    backTo ||
    (!shouldDisableBackBrowser && hasPreviousLocation(-1)) ||
    onClick ? (
      <IconButton
        size="medium"
        color="transparent"
        onClick={() => {
          if (onClick) {
            onClick();
          } else if (backTo) {
            navigate(backTo);
          } else {
            navigate(-1);
          }
        }}
        icon={faChevronLeft}
        aria-label={formatMessage(glossary.back)}
      />
    ) : (
      <>
        {!!isIosApp && (
          <IconButton
            size="medium"
            color="transparent"
            onClick={() => {
              navigateWithDeeplink();
            }}
            icon={faClose}
            aria-label={formatMessage(glossary.close)}
          />
        )}
      </>
    );

  const title = !!text && (
    <StyledLink
      to="."
      onClick={e => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      <Title as="p">
        {typeof text === 'string' ? text : <FormattedMessage {...text} />}
      </Title>
    </StyledLink>
  );

  const displayUserProfile = shouldDisableBackBrowser && !isLaptopAndAbove;

  return (
    <>
      {!!displayUserProfile && !!enableNavToggleProSet && (
        <Portal id="page-header-centered-item">
          <ProSetToggle />
        </Portal>
      )}
      <Portal id="page-header-title">
        {displayUserProfile ? (
          <Horizontal>
            <UserNav
              sport={sport}
              size={isLaptopAndAbove ? 'medium' : 'small'}
            />
            <UserCurrencies />
          </Horizontal>
        ) : (
          children || (
            <Horizontal>
              {backButton}
              {title}
              {customTitle}
            </Horizontal>
          )
        )}
      </Portal>
    </>
  );
};
